import { SxStyle } from '../../../../types';
import { useStringsContext } from '../../../../contexts/strings';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderClassNamePropType,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { Button, Typography } from '@mui/material';
import PlayerAvatar from '../../../common/player-avatar';
import GridOverlay from '../../../common/grid-overlay';
import AddIcon from '@mui/icons-material/Add';
import { StaffMemberInternal } from '../types';
import { translateCountry } from '../../../../countries';

const styles: SxStyle = {
  dataGridContainer: {
    flexGrow: 1,
    width: '100%',
    marginTop: 4,
  },
  dataGridHeader: {
    '& > .MuiDataGrid-columnSeparator--sideRight': {
      visibility: 'hidden',
    },
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
  textEllipse: {
    textOverflow: 'ellipsis', // TODO check double ellipse
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  link: {
    cursor: 'pointer',
  },
};

interface Props {
  staff: StaffMemberInternal[] | null;
  onAdd: () => void;
  onEdit: (member: StaffMemberInternal) => void;
  onDelete: (member: StaffMemberInternal) => void;
  loading: boolean;
}

interface ListAvatarProps {
  params: GridRenderCellParams;
  onClick: (member: StaffMemberInternal) => void;
}

interface EditButtonsProps extends Pick<Props, 'onEdit' | 'onDelete'> {
  params: GridRenderCellParams;
}

const ListAvatar = ({ params, onClick }: ListAvatarProps) => {
  const member: StaffMemberInternal = params.row;

  return (
    <Stack
      onClick={() => onClick(member)}
      sx={styles.link}
      spacing={4}
      direction="row"
      alignItems="center"
    >
      <PlayerAvatar fullName={member.fullName} srcImage={member.imageUrl} />
      <Typography variant="body2">{member.fullName}</Typography>
    </Stack>
  );
};

const ListEditButtons = ({ params, onEdit, onDelete }: EditButtonsProps) => (
  <Stack spacing={1} direction="row" alignItems="center">
    <IconButton onClick={() => onEdit(params.row)}>
      <EditIcon color="primary" />
    </IconButton>
    <IconButton onClick={() => onDelete(params.row)}>
      <DeleteIcon color="error" />
    </IconButton>
  </Stack>
);

const StaffList = ({ staff, onAdd, onEdit, onDelete, loading }: Props) => {
  const { strings } = useStringsContext();

  const defaultColumnOptions = {
    headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
    maxWidth: 300,
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: strings.staff.member,
      ...defaultColumnOptions,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => <ListAvatar onClick={onEdit} params={params} />,
    },
    {
      field: 'roleName',
      headerName: strings.staff.role,
      ...defaultColumnOptions,
    },
    {
      field: 'birthDay',
      headerName: strings.staff.birthDay,
      ...defaultColumnOptions,
    },
    {
      field: 'nationality',
      headerName: strings.staff.nationality,
      ...defaultColumnOptions,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? translateCountry(params.value as string) : '',
    },
    {
      field: 'clId',
      headerName: '',
      headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
      minWidth: 200,
      flex: 1,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButtons params={params} onEdit={onEdit} onDelete={onDelete} />
      ),
    },
  ];

  return (
    <Paper elevation={2} sx={styles.dataGridContainer}>
      <DataGrid
        loading={loading}
        sx={styles.dataGrid}
        rows={staff || []}
        columns={columns}
        getRowId={(row) => row.clId}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'name',
                sort: 'asc',
              },
            ],
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <GridOverlay>
              <Typography variant="italic">{strings.staff.empty}</Typography>
              <Button variant="contained" onClick={() => onAdd()} startIcon={<AddIcon />}>
                {strings.staff.addMember}
              </Button>
            </GridOverlay>
          ),
        }}
      />
    </Paper>
  );
};

export default StaffList;
